import { useState } from 'react';

import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { ApiError } from '@/lib/http/api-error';

import { useMutarPlanes } from '../planes.api';
import type { Plan } from '../planes.types';

interface PlanDuplicarDialogoProps {
  plan: Plan;
  onClose: () => void;
}

/** Mensaje de error legible para la mutación. */
function mensajeError(error: unknown): string {
  if (error instanceof ApiError) return error.message;

  return 'No se pudo duplicar el plan.';
}

/**
 * Duplica un plan con todos sus módulos.
 *
 * Se pide nombre y código nuevos (el del original ya está en uso); el resto de
 * datos se copian del plan de origen.
 */
export function PlanDuplicarDialogo({ plan, onClose }: PlanDuplicarDialogoProps) {
  const mutaciones = useMutarPlanes();

  const [nombre, setNombre] = useState(`${plan.nombre} (copia)`);
  const [codigo, setCodigo] = useState(`${plan.codigo}_COPIA`);
  const [errores, setErrores] = useState<Record<string, string>>({});
  const [aviso, setAviso] = useState<string | null>(null);

  const enviar = () => {
    const encontrados: Record<string, string> = {};

    if (!nombre.trim()) encontrados.nombre = 'El nombre es obligatorio.';
    if (!codigo.trim()) encontrados.codigo = 'El código es obligatorio.';

    setErrores(encontrados);
    setAviso(null);

    if (Object.keys(encontrados).length > 0) return;

    mutaciones.duplicar.mutate(
      { id: plan.id, payload: { nombre: nombre.trim(), codigo: codigo.trim().toUpperCase() } },
      { onSuccess: onClose, onError: (error) => setAviso(mensajeError(error)) },
    );
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={`Duplicar ${plan.nombre}`}
      description={`Se creará una copia del plan con sus ${plan.modulos} módulo(s).`}
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={mutaciones.duplicar.isPending}>
            Cancelar
          </Button>
          <Button onClick={enviar} loading={mutaciones.duplicar.isPending}>
            Duplicar plan
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {aviso ? (
          <Alert variant="error" title="No se pudo duplicar">
            {aviso}
          </Alert>
        ) : null}

        <Input
          label="Nombre de la copia"
          value={nombre}
          error={errores.nombre}
          onChange={(event) => setNombre(event.target.value)}
        />

        <Input
          label="Código de la copia"
          value={codigo}
          maxLength={50}
          error={errores.codigo}
          hint="En mayúsculas, sin repetirse."
          onChange={(event) => setCodigo(event.target.value.toUpperCase())}
        />
      </div>
    </Dialog>
  );
}
